/**
 * Initialize .hivemind folder structure for a project
 */

import { Project } from './storage'
import { convertToMoth, detectIntent } from './moth-converter'

export interface HivemindFiles {
  [path: string]: string
}

/**
 * Get the default .hivemind folder structure and content
 */
export function getHivemindTemplate(project: Project): HivemindFiles {
  const now = new Date().toISOString().split('T')[0]

  return {
    '.hivemind/config.json': JSON.stringify({
      version: '1.0.0',
      projectName: project.name,
      projectId: project.id,
      createdAt: project.createdAt,
      github: project.github || null,
      preferences: {
        autoSave: true,
        theme: 'dark',
      },
    }, null, 2),

    '.hivemind/memory.moth': `intent:project_memory
audience:ai_agents; version:1.0.0

[PRODUCT]
name:${project.name}
type:project
created:${now}

[USER_PREFERENCES]
# Add user preferences here

[ARCHITECTURE_DECISIONS]
# Document key decisions here

[TECHNICAL_NOTES]
# Add technical context here

[IMPORTANT_CONTEXT]
# Add important project context here
`,

    '.hivemind/commit-log.json': JSON.stringify([], null, 2),

    '.hivemind/tasks.md': `# Project Tasks

> This file tracks tasks and todos for the project.
> You can manually edit this or ask AI assistants to update it.

## Current Tasks

- [ ] Set up project structure
- [ ] Configure AI assistants

## Completed Tasks

- [x] Initialize .hivemind folder (${now})
`,

    '.hivemind/context/architecture.moth': `intent:system_architecture
audience:ai_agents; version:1.0.0

[PRODUCT]
name:${project.name}
type:project

[STACK]
# Document technology stack here

[COMPONENTS]
# Document key components here

[DATA_FLOW]
# Document data flow here
# Example: user -> api -> db

[INTEGRATIONS]
# Document third-party integrations here
`,

    '.hivemind/context/decisions.moth': `intent:architecture_decisions
audience:ai_agents; version:1.0.0

[ADR_001]
date:${now}
title:Project_Initialized
status:accepted

[ADR_001.context]
action:starting_new_project
assistant:hivemind_ai

[ADR_001.decision]
impl:.hivemind_folder
purpose:ai_context+project_metadata

[ADR_001.consequences]
pros:persistent_context;better_ai_assistance;version_controlled
`,

    '.hivemind/context/conventions.moth': `intent:coding_rules
audience:ai_agents; version:1.0.0

[STYLE]
# Document code style here
# Example: lang:typescript; fmt:prettier

[CONVENTIONS]
# Document naming conventions here
# Example: naming:camelCase_vars;PascalCase_types

[ARCHITECTURE]
# Document architectural patterns here

[TESTING]
# Document testing standards here

[FORBIDDEN]
# Document prohibited patterns here
# Example: no:any_type;console_log;var_keyword
`,

    '.hivemind/README.md': `# .hivemind Folder

This folder contains AI context and project metadata for HiveMind.

## Structure

- **config.json** - Project configuration and settings
- **memory.moth** - Long-term memories and user preferences (MOTH format)
- **commit-log.json** - History of commits made through HiveMind
- **tasks.md** - Project tasks and todos
- **context/** - Additional context files (MOTH format)
  - **architecture.moth** - System architecture notes
  - **decisions.moth** - Architecture Decision Records (ADRs)
  - **conventions.moth** - Code conventions and patterns
- **archive/** - Original markdown files (for reference only)

## MOTH Format

Context files use MOTH (Machine-Optimized Text Hierarchy) format for:
- 70-90% compression vs markdown
- Faster parsing by AI agents
- Lower token costs
- Structured, deterministic data

## Purpose

The .hivemind folder helps AI assistants:
- Remember important context across sessions
- Understand project structure and decisions
- Maintain consistency in code and architecture
- Track progress and tasks

## Usage

You can manually edit these files or ask AI assistants to update them.
For example: "Remember that we use TypeScript for all new files"

Learn more: https://github.com/huikku/hivemind
`,
  }
}

/**
 * Initialize .hivemind folder in a project's files
 */
export function initializeHivemindFolder(project: Project): void {
  const template = getHivemindTemplate(project)
  
  // Only add files that don't already exist
  for (const [path, content] of Object.entries(template)) {
    if (!project.files[path]) {
      project.files[path] = content
    }
  }
}

/**
 * Check if a project has .hivemind folder initialized
 */
export function hasHivemindFolder(project: Project): boolean {
  return '.hivemind/config.json' in project.files
}

/**
 * Auto-convert .hivemind markdown files to MOTH format in the background
 * This runs when opening a project and detects .md files that should be .moth
 *
 * @param project - The project to check and convert
 * @param onProgress - Optional callback for progress updates
 */
export async function autoConvertToMoth(
  project: Project,
  onProgress?: (message: string) => void
): Promise<void> {
  const conversions: Array<{ mdPath: string; mothPath: string; intent: string }> = [
    {
      mdPath: '.hivemind/memory.md',
      mothPath: '.hivemind/memory.moth',
      intent: 'project_memory',
    },
    {
      mdPath: '.hivemind/context/architecture.md',
      mothPath: '.hivemind/context/architecture.moth',
      intent: 'system_architecture',
    },
    {
      mdPath: '.hivemind/context/conventions.md',
      mothPath: '.hivemind/context/conventions.moth',
      intent: 'coding_rules',
    },
    {
      mdPath: '.hivemind/context/decisions.md',
      mothPath: '.hivemind/context/decisions.moth',
      intent: 'architecture_decisions',
    },
  ]

  let converted = 0

  for (const { mdPath, mothPath, intent } of conversions) {
    // Skip if .moth already exists or .md doesn't exist
    if (project.files[mothPath] || !project.files[mdPath]) {
      continue
    }

    try {
      onProgress?.(`Converting ${mdPath} to MOTH format...`)

      const mdContent = project.files[mdPath]
      const mothContent = await convertToMoth(mdContent, { intent })

      // Save the .moth file
      project.files[mothPath] = mothContent

      // Archive the original .md file
      const archivePath = mdPath.replace('.hivemind/', '.hivemind/archive/')
      project.files[archivePath] = mdContent

      // Remove the .md file from its original location
      delete project.files[mdPath]

      converted++
      onProgress?.(`✓ Converted ${mdPath} → ${mothPath}`)
    } catch (error) {
      console.error(`[MOTH] Failed to convert ${mdPath}:`, error)
      onProgress?.(`✗ Failed to convert ${mdPath}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  if (converted > 0) {
    onProgress?.(`🦋 Auto-converted ${converted} file(s) to MOTH format`)
  }
}

